import { useEffect, useState } from 'react';
import type { UserSettings } from '../../lib/storage';
import type { LlmProvider } from '../../lib/llm';
import { PrivacyInfo } from './PrivacyInfo';
import { Drawer } from './Drawer';

interface Props {
  settings: UserSettings | null;
  onSave: (
    next: Pick<UserSettings, 'mailto' | 'ncbiApiKey' | 'llmProvider' | 'geminiApiKey'>,
  ) => void;
}

export function SettingsPanel({ settings, onSave }: Props) {
  const [open, setOpen] = useState(false);
  const [mailto, setMailto] = useState(settings?.mailto ?? '');
  const [ncbiApiKey, setNcbiApiKey] = useState(settings?.ncbiApiKey ?? '');
  const [llmProvider, setLlmProvider] = useState<LlmProvider>(
    settings?.llmProvider ?? 'on-device',
  );
  const [geminiApiKey, setGeminiApiKey] = useState(settings?.geminiApiKey ?? '');

  useEffect(() => {
    if (settings) {
      setMailto(settings.mailto);
      setNcbiApiKey(settings.ncbiApiKey);
      setLlmProvider(settings.llmProvider);
      setGeminiApiKey(settings.geminiApiKey);
    }
  }, [settings]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="ms-btn ms-btn-secondary ms-btn-xs"
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        Settings
      </button>

      <Drawer open={open} onClose={() => setOpen(false)} title="Settings">
      <div>
        <label className="ms-eyebrow">
          Polite-pool email (optional)
        </label>
        <input
          type="email"
          value={mailto}
          onChange={(e) => setMailto(e.target.value)}
          placeholder="you@institution.edu"
          className="ms-input mt-1"
        />
        <p className="mt-0.5 text-[10px] text-slate-500">
          Passed to Crossref &amp; OpenAlex as <code>mailto=</code> for prioritised rate limits.
          Stays on your device.
        </p>
      </div>

      <div className="mt-2">
        <label className="ms-eyebrow">
          NCBI API key (optional)
        </label>
        <input
          type="password"
          value={ncbiApiKey}
          onChange={(e) => setNcbiApiKey(e.target.value)}
          placeholder="from ncbi.nlm.nih.gov/account/settings"
          className="ms-input mt-1"
        />
        <p className="mt-0.5 text-[10px] text-slate-500">
          Raises PubMed E-utilities rate limit from 3 to 10 req/s. Stored locally; sent only to
          eutils.ncbi.nlm.nih.gov.
        </p>
      </div>

      <div className="mt-4 border-t border-slate-200 pt-3">
        <label className="ms-eyebrow">AI enrichment engine</label>
        <select
          value={llmProvider}
          onChange={(e) => setLlmProvider(e.target.value as LlmProvider)}
          className="ms-input mt-1"
        >
          <option value="on-device">On-device (private, default) — runs in your browser</option>
          <option value="gemini">Google Gemini (cloud, BYOK) — sends text to Google</option>
        </select>
        <p className="mt-0.5 text-[10px] text-slate-500">
          The optional enrichment layer. On-device keeps everything local. Gemini is faster and
          stronger but the manuscript excerpt leaves your device.
        </p>

        {llmProvider === 'gemini' && (
          <div className="mt-2">
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-2 text-[11px] leading-relaxed text-amber-900">
              <strong>Data leaves your device in this mode.</strong> Enrichment prompts contain
              manuscript excerpts and are sent to Google&rsquo;s Gemini API. Google&rsquo;s free
              tier may use prompts for training — for unpublished work, use the on-device engine
              or a paid Google tier. The key is stored only in this browser and sent only to
              Google.
            </div>
            <label className="ms-eyebrow mt-2 block">Google Gemini API key (BYOK)</label>
            <input
              type="password"
              value={geminiApiKey}
              onChange={(e) => setGeminiApiKey(e.target.value)}
              placeholder="from aistudio.google.com/apikey"
              className="ms-input mt-1"
              autoComplete="off"
            />
            <p className="mt-0.5 text-[10px] text-slate-500">
              Create a free key in Google AI Studio. Stored locally; sent only to
              generativelanguage.googleapis.com. Never leaves with anyone but Google.
            </p>
          </div>
        )}
      </div>

      <div className="mt-3 flex justify-end gap-1">
        <button
          type="button"
          onClick={() => {
            onSave({
              mailto: mailto.trim(),
              ncbiApiKey: ncbiApiKey.trim(),
              llmProvider,
              geminiApiKey: geminiApiKey.trim(),
            });
            setOpen(false);
          }}
          className="ms-btn ms-btn-primary ms-btn-xs"
        >
          Save
        </button>
      </div>

      <details className="mt-3 rounded-lg border border-slate-200 bg-slate-50/70 p-2.5">
        <summary className="cursor-pointer text-xs font-semibold text-slate-700">
          Privacy &amp; network
        </summary>
        <div className="mt-2">
          <PrivacyInfo />
        </div>
      </details>
      </Drawer>
    </>
  );
}
